import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';
import './parallaximage.scss';
import { imageSources } from '../../../helpers/imageScales';
import { getParallaxMetrics } from './utils';

type ImageRef = {
  '@id': string;
  title?: string;
};

type ParallaxImageData = {
  image?: ImageRef[];
  image_alt?: string;
  height_desktop?: number | string;
  height_mobile?: number | string;
  focal_x?: number | string;
  focal_y?: number | string;
  [key: string]: any;
};

type Props = {
  data: ParallaxImageData;
  isEditMode?: boolean;
  imageSlot?: (args: {
    fieldId: string;
    value: any;
    element: ReactNode;
  }) => ReactNode | undefined;
};

const View = ({ data, isEditMode, imageSlot }: Props) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const sources =
    Array.isArray(data.image) && data.image.length > 0
      ? imageSources(data.image[0], 'great')
      : null;
  const imageUrl = sources?.src ?? null;

  const metrics = useMemo(() => getParallaxMetrics(data), [data]);
  const hasImage = Boolean(imageUrl);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = () => setPrefersReducedMotion(mediaQuery.matches);

    syncPreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncPreference);
      return () => mediaQuery.removeEventListener('change', syncPreference);
    }

    mediaQuery.addListener(syncPreference);
    return () => mediaQuery.removeListener(syncPreference);
  }, []);

  const sectionStyle = {
    '--parallax-height-desktop': `${metrics.heightDesktop}vh`,
    '--parallax-height-mobile': `${metrics.heightMobile}svh`,
  } as CSSProperties;

  const mediaStyle = {
    objectPosition: `${metrics.focalX}% ${metrics.focalY}%`,
  } as CSSProperties;

  const defaultImageElement = hasImage ? (
    <img
      className="parallax-image__media"
      src={imageUrl as string}
      srcSet={sources?.srcSet}
      sizes={sources?.sizes}
      alt={data.image_alt || ''}
      loading="lazy"
      style={mediaStyle}
    />
  ) : null;

  const slotted = imageSlot?.({
    fieldId: 'image',
    value: data.image,
    element: defaultImageElement,
  });

  const staticImageStage = hasImage ? (
    <div className="parallax-image__stage">
      <img
        className="parallax-image__media"
        src={imageUrl as string}
        srcSet={sources?.srcSet}
        sizes={sources?.sizes}
        alt={data.image_alt || ''}
        loading="lazy"
        style={mediaStyle}
      />
    </div>
  ) : null;

  const viewImage =
    hasImage && !prefersReducedMotion && !isEditMode ? (
      <Parallax
        className="parallax-image__stage"
        translateY={['-10%', '10%']}
        shouldAlwaysCompleteAnimation
      >
        <img
          className="parallax-image__media"
          src={imageUrl as string}
          srcSet={sources?.srcSet}
          sizes={sources?.sizes}
          alt={data.image_alt || ''}
          loading="lazy"
          style={mediaStyle}
        />
      </Parallax>
    ) : (
      staticImageStage
    );

  const viewport = (
    <div className="parallax-image__viewport">
      {isEditMode ? slotted ?? defaultImageElement : viewImage}
      <div className="parallax-image__veil" aria-hidden="true" />
      {!hasImage && isEditMode && (
        <div className="parallax-image__empty-label">
          Sélectionnez une image parallax
        </div>
      )}
    </div>
  );

  if (!hasImage && !slotted) {
    if (!isEditMode) return null;

    return (
      <section
        ref={sectionRef}
        className="parallax-image parallax-image--empty"
        style={sectionStyle}
      >
        <div className="parallax-image__viewport">
          <div className="parallax-image__empty-label">
            Sélectionnez une image parallax
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={`parallax-image${
        prefersReducedMotion ? ' parallax-image--reduced-motion' : ''
      }${!hasImage ? ' parallax-image--empty' : ''}`}
      style={sectionStyle}
    >
      {hasImage && !prefersReducedMotion && !isEditMode ? (
        <ParallaxProvider>{viewport}</ParallaxProvider>
      ) : (
        viewport
      )}
    </section>
  );
};

export default View;
